/**
 * Typed GraphQL client for the partner portal.
 *
 * Operations are written with `graphql()` from gql.tada, which type-checks every
 * field selection against the backend's generated SDL
 * (`reloaded-rest/schema.graphql`, wired up in `tsconfig.json` +
 * `src/graphql-env.d.ts`). Selecting a field that doesn't exist — or misusing a
 * variable — is a compile-time error, and `ResultOf<typeof doc>` gives the exact
 * response shape with no hand-written interfaces.
 *
 * Transport mirrors `src/lib/auth.ts`: authenticated POSTs to the pop.in GraphQL
 * endpoint, carrying the Firebase ID token and the `mighty` app id.
 */

import { print } from 'graphql';
import { initGraphQLTada, type TadaDocumentNode } from 'gql.tada';
import { getIdToken } from 'firebase/auth';

import type { introspection } from '@/graphql-env.d.ts';
import { GRAPHQL_ENDPOINT } from '@/config';
import { getFirebaseAuth } from '@/lib/firebase';
import { BackendUnavailableError } from '@/lib/backend-error';
import { requestHeaders, type RequestViewer } from '@/lib/request-headers';

/**
 * The `graphql()` document builder, bound to the backend schema. Custom scalars
 * are mapped to their TS representation (the backend's `Timestamp` is epoch ms).
 */
export const graphql = initGraphQLTada<{
  introspection: introspection;
  // App code (not a library) — unmask fragments so composed selections are
  // directly readable on the result without `readFragment`.
  disableMasking: true;
  scalars: {
    Timestamp: number;
    ID: string;
  };
}>();

export type { FragmentOf, ResultOf, VariablesOf } from 'gql.tada';

/** Error thrown when the GraphQL response carries an `errors` array. */
export class GraphQLRequestError extends Error {
  constructor(message: string, public readonly errors: Array<{ message: string }>) {
    super(message);
    this.name = 'GraphQLRequestError';
  }

  /** The error for a response's `errors`, their messages joined. */
  static from(errors: Array<{ message: string }>): GraphQLRequestError {
    return new GraphQLRequestError(errors.map((e) => e.message).join(', '), errors);
  }
}

/** Per-call transport options. `signal` aborts the underlying `fetch`. */
export interface RequestOptions {
  signal?: AbortSignal;
}

/** Variables (required iff the operation declares them), then optional
 *  {@link RequestOptions}. An operation with no variables passes `undefined`
 *  before the options. */
type RequestArgs<Variables> = Variables extends Record<string, never>
  ? [variables?: undefined, options?: RequestOptions]
  : [variables: Variables, options?: RequestOptions];

/** One entry of a response's `errors`. `path` names the field that failed,
 *  an alias first, when the error belongs to one. */
export interface GraphQLErrorEntry {
  message: string;
  path?: readonly (string | number)[];
}

interface GraphQLResponse<T> {
  data?: T | null;
  errors?: GraphQLErrorEntry[];
}

/**
 * The one authenticated POST every call goes through — carries the Firebase
 * ID token + the `mighty` app id, and returns the `{ data, errors }` envelope
 * as sent. Takes an already-printed query string.
 */
async function postGraphQLRaw<Result>(
  query: string,
  variables: Record<string, unknown>,
  viewer?: RequestViewer,
  options?: RequestOptions,
): Promise<GraphQLResponse<Result>> {
  if (!GRAPHQL_ENDPOINT) {
    throw new Error('BUN_PUBLIC_GRAPHQL_ENDPOINT is not configured.');
  }

  const auth = getFirebaseAuth();
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Must be signed in to call the pop.in GraphQL API.');
  }
  const token = await getIdToken(currentUser);

  let response: Response;
  try {
    response = await fetch(GRAPHQL_ENDPOINT, {
      method: 'POST',
      headers: requestHeaders(token, viewer),
      body: JSON.stringify({ query, variables }),
      signal: options?.signal,
    });
  } catch (error) {
    throw new BackendUnavailableError('Could not reach the pop.in GraphQL endpoint.', { cause: error });
  }

  if (!response.ok) {
    throw new BackendUnavailableError(`GraphQL request failed (HTTP ${response.status}).`, { status: response.status });
  }

  return (await response.json()) as GraphQLResponse<Result>;
}

/** {@link postGraphQLRaw}, unwrapped: any `errors` entry fails the call. */
async function postGraphQL<Result>(
  query: string,
  variables: Record<string, unknown>,
  viewer?: RequestViewer,
  options?: RequestOptions,
): Promise<Result> {
  const body = await postGraphQLRaw<Result>(query, variables, viewer, options);
  if (body.errors?.length) {
    throw GraphQLRequestError.from(body.errors);
  }
  if (!body.data) {
    throw new Error('GraphQL response contained no data.');
  }
  return body.data;
}

/**
 * Execute a typed operation. The result type is inferred from the document, so
 * callers get a fully-typed `data` with no casting.
 *
 * Variables are required iff the operation declares them.
 */
export async function gqlRequest<Result, Variables>(
  document: TadaDocumentNode<Result, Variables>,
  ...[variables, options]: RequestArgs<Variables>
): Promise<Result> {
  return postGraphQL<Result>(print(document), (variables ?? {}) as Record<string, unknown>, undefined, options);
}

/**
 * {@link gqlRequest} through the staff door: a super admin's call carries
 * `X-Act-As-Admin: true` so admin-only reads (e.g. `admin_allPlaces`) resolve;
 * anyone else's goes out exactly like {@link gqlRequest}. See `requestHeaders`.
 */
export async function gqlRequestAs<Result, Variables>(
  viewer: RequestViewer,
  document: TadaDocumentNode<Result, Variables>,
  ...[variables, options]: RequestArgs<Variables>
): Promise<Result> {
  return postGraphQL<Result>(print(document), (variables ?? {}) as Record<string, unknown>, viewer, options);
}

/**
 * {@link gqlRequestAs}, keeping a partial answer: when the response carries
 * data, its `errors` come back beside it instead of failing the call (an
 * aliased read where one alias failed reads null there, the others stand).
 * With no data it throws, as {@link gqlRequestAs} does.
 */
export async function gqlRequestPartialAs<Result, Variables>(
  viewer: RequestViewer,
  document: TadaDocumentNode<Result, Variables>,
  ...[variables, options]: RequestArgs<Variables>
): Promise<{ data: Result; errors: GraphQLErrorEntry[] }> {
  const body = await postGraphQLRaw<Result>(print(document), (variables ?? {}) as Record<string, unknown>, viewer, options);
  if (!body.data) {
    if (body.errors?.length) throw GraphQLRequestError.from(body.errors);
    throw new Error('GraphQL response contained no data.');
  }
  return { data: body.data, errors: body.errors ?? [] };
}
